import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { Input } from '@eika/input';
import { Label } from '@eika/label';
import { RadioButtonPanelOptions, RadioButtonPanels } from '@eika/radio-button';

import { useDebitAccountsQuery } from '@/react-query/hooks/query/accounts/use-debit-accounts';
import { useCard } from '@/react-query/hooks/query/use-card';
import { useMultipleCustomersFromAccounts } from '@/react-query/hooks/query/use-customer';

import { clean, getCloseCreditAccountUrl } from '@components/close-credit-account/functions';
import closeCreditAccountRoutes from '@components/close-credit-account/routes';
import Footer from '@components/footer/Footer';
import SkeletonList from '@components/skeleton/SkeletonList';
import { CardType } from '@generated-types';
import useText from '@hooks/use-text';
import { formatAccountNumber, isValidAccountNumber } from '@utils/account-utils';

const CloseCreditCardAccountTransferForm = () => {
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();

    const {
        transferFormOptionCustom,
        transferFormOptionNone,
        transferFormRadioButtonsLabel,
        transferFormOptionCustomLabel,
        transferFormOptionNoneLabel,
        transferFormInvalidAccountNumber,
    } = useText();
    const { summaryUrl } = closeCreditAccountRoutes;

    const { data: card } = useCard();
    const { transactionsAccount, closeCreditAccountFormCustomAccountOption, transferFormSharedBy } = useText();
    const { data: debitAccounts } = useDebitAccountsQuery();
    const account = queryParams.get('account');
    const [selectedOption, setSelectedOption] = React.useState<string>(account || transferFormOptionNone);
    const [customAccountNumberError, setCustomAccountNumberError] = useState<string | undefined>(undefined);
    const [customAccountNumber, setCustomAccountNumber] = useState<string>('');

    const ownerId = card?.accountOwner;
    const allAccounts = [...new Set(debitAccounts)];
    const hasMultipleAccounts = allAccounts.length >= 1;
    const uniqueAccounts = [...new Map(allAccounts.map((account) => [account.owner, account])).values()];

    const { data: allCustomerAccounts, isPending: allCustomersPending } = useMultipleCustomersFromAccounts(
        CardType.DEBIT,
        uniqueAccounts,
    );
    const [updatedSearchParams] = useSearchParams();

    const next = () => {
        if (selectedOption === transferFormOptionNone) {
            navigate(getCloseCreditAccountUrl(updatedSearchParams, summaryUrl));
        }
        if (selectedOption === transferFormOptionCustom && !isValidAccountNumber(customAccountNumber)) {
            setCustomAccountNumberError(transferFormInvalidAccountNumber);
            return;
        }
        if (selectedOption === transferFormOptionCustom) {
            updatedSearchParams.set('account', customAccountNumber);
        } else {
            updatedSearchParams.set('account', selectedOption);
        }
        navigate(getCloseCreditAccountUrl(updatedSearchParams, summaryUrl, updatedSearchParams.toString()));
    };

    const handleInput = (input: string) => {
        if (customAccountNumber.length > input.length) {
            setCustomAccountNumberError('');
        }
        setCustomAccountNumber(input);
    };

    useEffect(() => {
        if (customAccountNumber && selectedOption !== transferFormOptionCustom) {
            setCustomAccountNumber('');
            setCustomAccountNumberError('');
        }
    }, [customAccountNumber, selectedOption, transferFormOptionCustom]);

    const options: RadioButtonPanelOptions[] = [
        ...allAccounts.map((account) => {
            const customer = allCustomerAccounts?.find((customer) => customer.number === account.owner);
            const isShared = account.owner !== ownerId;
            const labelText = transferFormSharedBy
                .replace('{firstName}', customer?.firstName || '')
                .replace('{lastName}', customer?.lastName || '');

            return {
                value: account.number,
                label: account.agreementName ?? account.productName ?? formatAccountNumber(account.number),
                labels: isShared && customer ? <Label type="warning" size="small" text={labelText} /> : undefined,
                description: formatAccountNumber(account.number),
                key: account.number,
            };
        }),
        {
            value: transferFormOptionCustom,
            label: transferFormOptionCustomLabel,
            key: 'custom-option',
        },
        {
            value: transferFormOptionNone,
            label: transferFormOptionNoneLabel,
            key: 'none-option',
        },
    ];
    return (
        <FormContainer>
            <FormContainer.Content>
                {hasMultipleAccounts && allCustomersPending ? (
                    <SkeletonList />
                ) : (
                    <RadioButtonPanels
                        label={transferFormRadioButtonsLabel}
                        options={options}
                        selectedOption={selectedOption}
                        onChange={(e) => setSelectedOption(e)}
                    />
                )}

                {selectedOption === transferFormOptionCustom && (
                    <div className="custom-account-number-input-container">
                        <Input
                            error={customAccountNumberError}
                            id="custom-account-number-input"
                            inputMode="numeric"
                            label={closeCreditAccountFormCustomAccountOption}
                            maxLength={11}
                            minLength={11}
                            onChange={({ target }) => handleInput(clean(target.value))}
                            placeholder={transactionsAccount}
                            value={customAccountNumber}
                        />
                    </div>
                )}
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer next={next} disabled={!!customAccountNumberError} />
            </FormContainer.Footer>
        </FormContainer>
    );
};

export default CloseCreditCardAccountTransferForm;
